import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import api, { formatApiError, getTenantSubdomain } from '../services/api';
import { getImputationEtatOptions, getEtatBadgeClasses, getEtatLabel } from '../utils/imputationEtats';
import Toast from './Toast';
import useToast from '../hooks/useToast';

const EmployeeImputationsPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('employee');
  const { t: tCommon } = useTranslation('common');
  
  // State for imputations list
  const [imputations, setImputations] = useState([]);
  const [imputationsCount, setImputationsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showComments, setShowComments] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [loadingImputations, setLoadingImputations] = useState(false);
  const [imputationsError, setImputationsError] = useState(null);

  // State for filters
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    operation: '',
    etat_final: '',
    gcr_prefix: '',
    gcr_code: '',
    production_line_lp: '',
    project_type: '',
    program: ''
  });

  // State for operations (for dropdowns)
  const [operations, setOperations] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);

  // State for modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedImputation, setSelectedImputation] = useState(null);

  // State for operation search dropdown (for filter)
  const [filterOperationSearch, setFilterOperationSearch] = useState('');
  const [showFilterOperationDropdown, setShowFilterOperationDropdown] = useState(false);

  // State for sorting
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // State for operation details modal
  const [showOperationModal, setShowOperationModal] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);

  // State for notifications
  const { toast, showSuccess, showError, showWarning, hideToast } = useToast();
  const [deleting, setDeleting] = useState(false);

  // Refs for click outside detection
  const filterOperationRef = useRef(null);

  const etatChoices = React.useMemo(() => getImputationEtatOptions(getTenantSubdomain()), []);

  const getEtatDisplay = (etat) => getEtatLabel(etat, getTenantSubdomain());

  // Filter operations based on search; MULTI-DOMAINE operations always appear (not filtered by search)
  const getFilteredOperations = (searchTerm) => {
    if (!searchTerm.trim()) return operations;
    const term = searchTerm.toLowerCase();
    const matching = operations.filter(op =>
      op.production_line_lp?.toLowerCase().includes(term) || op.activity?.toLowerCase().includes(term)
    );
    const multiDomaine = operations.filter(op =>
      op.domain && String(op.domain).toUpperCase().includes('MULTI-DOMAINE')
    );
    const seen = new Set();
    return [...matching, ...multiDomaine].filter(op => {
      if (seen.has(op.id)) return false;
      seen.add(op.id);
      return true;
    });
  };

  // Fetch operations
  const fetchOperations = async () => {
    setLoadingOperations(true);
    try {
      const response = await api.getOperations({ status: 'active' }, 1, 1000);
      setOperations(response.results || []);
    } catch (error) {
      console.error('Error fetching operations:', error);
    } finally {
      setLoadingOperations(false);
    }
  };

  // Derive unique LP, Program, Project type from operations for filter dropdowns
  const filterLPOptions = [...new Set((operations || []).map(op => op.production_line_lp).filter(Boolean))].sort();
  const filterProgramOptions = [...new Set((operations || []).map(op => op.program).filter(Boolean))].sort();
  const filterProjectTypeOptions = [...new Set((operations || []).map(op => op.production_type).filter(Boolean))].sort();

  // Fetch imputations
  const fetchImputations = async () => {
    setLoadingImputations(true);
    setImputationsError(null);
    try {
      // Build filters - remove GCR filters from API call as we'll filter client-side
      const { gcr_prefix, gcr_code, ...apiFilters } = filters;
      if (!apiFilters.operation && filterOperationSearch.trim()) {
        // If searching by text but no operation selected, we'll filter client-side
        // For now, fetch all and filter client-side
      }
      const response = await api.getImputations(apiFilters, currentPage, pageSize);
      let results = response.results || [];
      
      // Apply client-side filtering for operation search if no specific operation is selected
      if (!apiFilters.operation && filterOperationSearch.trim()) {
        const searchTerm = filterOperationSearch.toLowerCase();
        results = results.filter(imp => {
          if (!imp.operation) return false;
          const opCode = imp.operation.production_line_lp?.toLowerCase() || '';
          const opName = imp.operation.activity?.toLowerCase() || '';
          return opCode.includes(searchTerm) || opName.includes(searchTerm);
        });
      }
      
      // Apply client-side filtering for GCR/GIR prefix
      if (gcr_prefix) {
        results = results.filter(imp => {
          if (!imp.gcr) return false;
          return imp.gcr.startsWith(gcr_prefix);
        });
      }
      
      // Apply client-side filtering for GCR/GIR code
      if (gcr_code.trim()) {
        const codeSearch = gcr_code.toLowerCase().trim();
        results = results.filter(imp => {
          if (!imp.gcr) return false;
          // Extract the code part after the prefix (e.g., "GCR-12345" -> "12345")
          const gcrLower = imp.gcr.toLowerCase();
          if (gcrLower.includes('-')) {
            const codePart = gcrLower.split('-').slice(1).join('-');
            return codePart.includes(codeSearch);
          } else {
            // If no prefix, search in the whole GCR
            return gcrLower.includes(codeSearch);
          }
        });
      }
      
      // Update count for filtered results if client-side filtering was applied
      if ((!apiFilters.operation && filterOperationSearch.trim()) || gcr_prefix || gcr_code.trim()) {
        setImputationsCount(results.length);
      } else {
        setImputationsCount(response.count || 0);
      }
      
      setImputations(results);
    } catch (error) {
      const formattedError = formatApiError(error);
      setImputationsError(formattedError.generalError || t('imputations.loadFailed'));
      console.error('Error fetching imputations:', error);
    } finally {
      setLoadingImputations(false);
    }
  };

  // Fetch operations on mount
  useEffect(() => {
    fetchOperations();
  }, []);

  // Fetch imputations on mount and when filters/page change
  useEffect(() => {
    fetchImputations();
  }, [filters, currentPage, pageSize, filterOperationSearch]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.date_from, filters.date_to, filters.operation, filters.etat_final, filters.gcr_prefix, filters.gcr_code, filters.production_line_lp, filters.project_type, filters.program, filterOperationSearch]);

  // Handle click outside for operation dropdown (filter only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterOperationRef.current && !filterOperationRef.current.contains(event.target)) {
        setShowFilterOperationDropdown(false);
      }
    };

    if (showFilterOperationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterOperationDropdown]);

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      operation: '',
      etat_final: '',
      gcr_prefix: '',
      gcr_code: '',
      production_line_lp: '',
      project_type: '',
      program: ''
    });
    setFilterOperationSearch('');
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle navigate to create page
  const handleCreateClick = () => {
    navigate('/employee-dashboard/imputations/create');
  };

  // Handle navigate to edit page
  const handleEditClick = (imputation) => {
    navigate(`/employee-dashboard/imputations/edit/${imputation.id}`);
  };

  // Handle delete click
  const handleDeleteClick = (imputation) => {
    setSelectedImputation(imputation);
    setShowDeleteModal(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!selectedImputation) return;

    setDeleting(true);
    
    // Optimistically remove from UI
    const imputationToDelete = selectedImputation;
    setImputations(prev => prev.filter(imp => imp.id !== imputationToDelete.id));
    setImputationsCount(prev => Math.max(0, prev - 1));
    setShowDeleteModal(false);

    try {
      await api.deleteImputation(imputationToDelete.id);
      showSuccess(t('imputations.entryDeleted'), t('imputations.successDeleted'));
    } catch (error) {
      // Revert on error
      setImputations(prev => [...prev, imputationToDelete].sort((a, b) => b.id - a.id));
      setImputationsCount(prev => prev + 1);
      const formattedError = formatApiError(error);
      showError(t('imputations.deleteFailed'), formattedError.generalError || t('imputations.deleteFailedMsg'));
      
      // Show API warnings if present
      if (error.response?.status === 403) {
        showWarning(t('imputations.permissionDenied'), t('imputations.permissionDeniedDelete'), 5000);
      } else if (error.response?.status === 404) {
        showWarning(t('imputations.notFoundTitle'), t('imputations.notFoundMsg'), 4000);
      }
    } finally {
      setDeleting(false);
      setSelectedImputation(null);
    }
  };

  // Calculate état counts
  const calculateEtatCounts = () => {
    const counts = {};
    etatChoices.forEach(choice => {
      counts[choice.value] = imputations.filter(imp => imp.etat_final === choice.value).length;
    });
    return counts;
  };

  const etatCounts = calculateEtatCounts();

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted imputations
  const getSortedImputations = () => {
    if (!sortColumn) return imputations;

    return [...imputations].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'date_operation':
          aValue = a.date_operation ? new Date(a.date_operation).getTime() : 0;
          bValue = b.date_operation ? new Date(b.date_operation).getTime() : 0;
          break;
        case 'date_saisie':
          aValue = a.date_saisie ? new Date(a.date_saisie).getTime() : 0;
          bValue = b.date_saisie ? new Date(b.date_saisie).getTime() : 0;
          break;
        case 'temps_standard':
          aValue = a.temps_standard ? parseFloat(a.temps_standard) : 0;
          bValue = b.temps_standard ? parseFloat(b.temps_standard) : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle operation click
  const handleOperationClick = (operation) => {
    setSelectedOperation(operation);
    setShowOperationModal(true);
  };

  // SortableHeader component
  const SortableHeader = ({ column, children, className = '' }) => {
    const isActive = sortColumn === column;
    return (
      <th 
        className={`px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors select-none ${className}`}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-2">
          <span>{children}</span>
          <div className="flex flex-col">
            <span className={`material-symbols-outlined text-[14px] leading-none ${
              isActive && sortDirection === 'asc' ? 'text-primary' : 'text-slate-300'
            }`}>
              arrow_drop_up
            </span>
            <span className={`material-symbols-outlined text-[14px] leading-none -mt-1 ${
              isActive && sortDirection === 'desc' ? 'text-primary' : 'text-slate-300'
            }`}>
              arrow_drop_down
            </span>
          </div>
        </div>
      </th>
    );
  };

  // Check if imputation can be edited (less than 24 hours)
  const canEditImputation = (imputation) => {
    if (!imputation.date_saisie) return false;
    const saisieDate = new Date(imputation.date_saisie);
    const now = new Date();
    const hoursDiff = (now - saisieDate) / (1000 * 60 * 60);
    return hoursDiff < 24;
  };

  // Pagination calculations
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, imputationsCount);
  const totalPages = Math.ceil(imputationsCount / pageSize);

  // Generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      if (totalPages > 1) pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Heading */}
      <div className="flex flex-wrap justify-between items-end gap-4 px-1">
        <div className="flex min-w-72 flex-col gap-2">
          <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-[-0.02em]">
            {t('imputations.titleShort')}
          </h1>
          <p className="text-slate-500 text-base font-normal leading-normal">
            {t('imputations.subtitleManage')}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowComments((v) => !v)}
            className={`flex items-center justify-center gap-2 rounded-lg h-10 px-4 text-sm font-semibold border shadow-sm transition-colors ${
              showComments
                ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20 dark:bg-primary/20 dark:border-primary/50 dark:hover:bg-primary/30'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700'
            }`}
            title={showComments ? t('imputations.hideComments') : t('imputations.showComments')}
          >
            <span className="material-symbols-outlined text-[20px]">comment</span>
            <span>{showComments ? t('imputations.hideCommentsLabel') : t('imputations.showCommentsLabel')}</span>
          </button>
          <button
            onClick={handleCreateClick}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold shadow-md hover:bg-primary-hover transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            <span>{t('imputations.newImputation')}</span>
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        duration={toast.duration}
      />

      {/* Filters & Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-end">
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.dateFrom')}</p>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.dateTo')}</p>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.workflow')}</p>
            <div className="relative">
              <select
                value={filters.gcr_prefix}
                onChange={(e) => setFilters({ ...filters, gcr_prefix: e.target.value })}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                <option value="">{tCommon('all')}</option>
                <option value="GCR">GCR</option>
                <option value="GIR">TT GIR</option>
                <option value="PROMO">PROMO</option>
                <option value="BYNOW">BYNOW</option>
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.code')}</p>
            <input
              type="text"
              value={filters.gcr_code}
              onChange={(e) => setFilters({ ...filters, gcr_code: e.target.value })}
              placeholder={t('imputations.enterCode')}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary placeholder-slate-400"
            />
          </div>
          <div className="flex flex-col gap-2 relative" ref={filterOperationRef}>
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.operation')}</p>
            <div className="relative">
              <input
                type="text"
                value={filterOperationSearch}
                onChange={(e) => {
                  setFilterOperationSearch(e.target.value);
                  setShowFilterOperationDropdown(true);
                }}
                onFocus={() => setShowFilterOperationDropdown(true)}
                placeholder={t('imputations.searchOperation')}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary placeholder-slate-400"
              />
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-400 text-[20px] pointer-events-none">search</span>
              
              {showFilterOperationDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  <div
                    onClick={() => {
                      setFilters({ ...filters, operation: '' });
                      setFilterOperationSearch('');
                      setShowFilterOperationDropdown(false);
                    }}
                    className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 font-medium text-slate-700"
                  >
                    {t('imputations.allOperations')}
                  </div>
                  {getFilteredOperations(filterOperationSearch).map(op => (
                    <div
                      key={op.id}
                      onClick={() => {
                        setFilters({ ...filters, operation: op.id.toString() });
                        setFilterOperationSearch(`${op.production_line_lp} - ${op.activity}`);
                        setShowFilterOperationDropdown(false);
                      }}
                      className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 last:border-b-0"
                    >
                      <div className="font-medium text-slate-900">{op.production_line_lp} - {op.activity}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.lp')}</p>
            <div className="relative">
              <select
                value={filters.production_line_lp}
                onChange={(e) => setFilters({ ...filters, production_line_lp: e.target.value })}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                <option value="">{tCommon('all')}</option>
                {filterLPOptions.map(lp => (
                  <option key={lp} value={lp}>{lp}</option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.program')}</p>
            <div className="relative">
              <select
                value={filters.program}
                onChange={(e) => setFilters({ ...filters, program: e.target.value })}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                <option value="">{tCommon('all')}</option>
                {filterProgramOptions.map(pg => (
                  <option key={pg} value={pg}>{pg}</option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.projectType')}</p>
            <div className="relative">
              <select
                value={filters.project_type}
                onChange={(e) => setFilters({ ...filters, project_type: e.target.value })}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                <option value="">{tCommon('all')}</option>
                {filterProjectTypeOptions.map(pt => (
                  <option key={pt} value={pt}>{pt}</option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.etatFinal')}</p>
            <div className="relative">
              <select
                value={filters.etat_final}
                onChange={(e) => setFilters({ ...filters, etat_final: e.target.value })}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                <option value="">{t('imputations.allStates')}</option>
                {etatChoices.map(choice => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label} {etatCounts[choice.value] > 0 ? `(${etatCounts[choice.value]})` : ''}
                  </option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
        </div>
        {(filters.date_from || filters.date_to || filters.gcr_prefix || filters.gcr_code || filterOperationSearch || filters.operation || filters.etat_final || filters.production_line_lp || filters.project_type || filters.program) && (
          <div className="mt-4">
            <button
              onClick={handleClearFilters}
              className="h-10 px-4 text-sm font-medium text-primary hover:text-blue-700 transition-colors"
            >
              {t('imputations.clearAllFilters')}
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {imputationsError && (
        <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 px-4 py-3 rounded-lg flex items-center gap-2">
          <span className="material-symbols-outlined text-[20px]">error</span>
          <span className="text-sm font-medium">{imputationsError}</span>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6 items-start">
        {/* Imputations Table */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">{t('imputations.management')}</h3>
              <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-1 rounded dark:bg-slate-700 dark:text-slate-300">
                {imputationsCount === 1 ? t('imputations.countOne', { count: imputationsCount }) : t('imputations.countMany', { count: imputationsCount })}
              </span>
            </div>
            {imputationsError ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-red-500">{imputationsError}</div>
              </div>
            ) : imputations.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-slate-500">{t('imputations.noImputationsFound')}</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm table-auto whitespace-nowrap">
                  <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                    <tr>
                      <SortableHeader column="date_operation" className="px-4 py-3 min-w-[120px]">{t('imputations.dateOperation')}</SortableHeader>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">{t('imputations.operationId')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[180px]">{t('imputations.operation')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">{t('imputations.lp')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">{t('imputations.program')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">{t('imputations.projectType')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">{t('imputations.etat')}</th>
                      <SortableHeader column="temps_standard" className="px-4 py-3 min-w-[120px]">{t('imputations.tempsStandard')}</SortableHeader>
                      <SortableHeader column="date_saisie" className="px-4 py-3 min-w-[150px]">{t('imputations.dateSaisie')}</SortableHeader>
                      <th className="px-4 py-3 font-semibold text-center min-w-[180px]">{t('imputations.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {getSortedImputations().map((imputation) => (
                      <React.Fragment key={imputation.id}>
                        <motion.tr
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">
                            {imputation.date_operation ? new Date(imputation.date_operation).toLocaleDateString(getDateLocale(i18n.language)) : '-'}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">
                            {imputation.gcr || '-'}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">
                            {imputation.operation ? (
                              <div 
                                className="cursor-pointer hover:text-primary transition-colors"
                                onClick={() => handleOperationClick(imputation.operation)}
                              >
                                <div className="font-medium text-slate-900">{imputation.operation.production_line_lp}</div>
                                <div className="text-xs text-slate-500">{imputation.operation.activity}</div>
                              </div>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">{imputation.operation?.production_line_lp ?? '—'}</td>
                          <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">{imputation.operation?.program ?? '—'}</td>
                          <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">{imputation.operation?.production_type ?? '—'}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${getEtatBadgeClasses(imputation.etat_final, getTenantSubdomain())}`}>
                              {getEtatDisplay(imputation.etat_final)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">
                            {imputation.temps_standard || '-'}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">
                            {imputation.date_saisie ? new Date(imputation.date_saisie).toLocaleString(getDateLocale(i18n.language)) : '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2.5">
                              <button
                                onClick={() => handleEditClick(imputation)}
                                disabled={!canEditImputation(imputation)}
                                className={`h-9 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm border ${
                                  canEditImputation(imputation)
                                    ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70 dark:hover:bg-blue-900/60'
                                    : 'bg-gradient-to-r from-slate-50 to-slate-100/50 text-slate-400 border-slate-300/50 dark:from-slate-800 dark:to-slate-700 dark:text-slate-500 dark:border-slate-600 cursor-not-allowed opacity-60'
                                }`}
                                title={canEditImputation(imputation) ? t('imputations.edit') : t('imputations.editLockedTitle')}
                              >
                              <span className="material-symbols-outlined text-[16px]">
                                {canEditImputation(imputation) ? 'edit' : 'lock'}
                              </span>
                                <span className="font-medium">{tCommon('edit')}</span>
                              </button>
                              <button
                                onClick={() => handleDeleteClick(imputation)}
                                className="h-9 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm bg-gradient-to-r from-red-50 to-red-100/50 text-red-600 border border-red-300/50 hover:from-red-100 hover:to-red-200 dark:from-red-900/30 dark:to-red-800/20 dark:text-red-200 dark:border-red-700/70 dark:hover:from-red-800/50 dark:hover:to-red-700/40"
                                title={t('imputations.delete')}
                              >
                                <span className="material-symbols-outlined text-[16px]">delete_outline</span>
                                <span className="font-medium">{tCommon('delete')}</span>
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                        {showComments && imputation.commentaire && (
                          <tr className="bg-slate-50/70 hover:bg-slate-50">
                            <td colSpan={10} className="px-4 py-2 text-sm text-slate-600 border-t-0 align-top">
                              <span className="font-medium text-slate-500 mr-2">{t('imputations.comment')}:</span>
                              {imputation.commentaire}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Pagination */}
            {imputations.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-500">
                    {t('imputations.showingRange', { from: imputationsCount > 0 ? startIndex : 0, to: endIndex, total: imputationsCount })}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">{tCommon('perPage')}</span>
                    <div className="relative">
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="appearance-none rounded border border-slate-200 bg-white px-3 py-1.5 pr-8 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-[16px] pointer-events-none">arrow_drop_down</span>
                    </div>
                  </div>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={t('imputations.firstPage')}
                    >
                      <span className="material-symbols-outlined text-sm">first_page</span>
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={t('imputations.previousPage')}
                    >
                      <span className="material-symbols-outlined text-sm">chevron_left</span>
                    </button>
                    
                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                          <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`h-8 w-8 flex items-center justify-center rounded border text-sm font-medium transition-colors ${
                              currentPage === page
                                ? 'bg-primary text-white border-primary'
                                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={t('imputations.nextPage')}
                    >
                      <span className="material-symbols-outlined text-sm">chevron_right</span>
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage >= totalPages}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={t('imputations.lastPage')}
                    >
                      <span className="material-symbols-outlined text-sm">last_page</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Operation Details Modal */}
      <AnimatePresence>
        {showOperationModal && selectedOperation && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => {
                setShowOperationModal(false);
                setSelectedOperation(null);
              }}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full border border-slate-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white z-10 relative">
                  <div className="flex-1 pr-12">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('imputations.operationDetails')}</p>
                    <h3 className="text-xl font-bold text-slate-900">{selectedOperation.production_line_lp} - {selectedOperation.activity}</h3>
                    <p className="text-sm text-slate-500">
                      {t('imputations.operationDetailsSubtitle')}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowOperationModal(false);
                      setSelectedOperation(null);
                    }}
                    className="absolute top-6 right-6 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1"
                    title={t('imputations.closeModal')}
                  >
                    <span className="material-symbols-outlined text-[22px] group-hover:scale-110 transition-transform duration-200">close</span>
                  </button>
                </div>
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-900 block">{t('imputations.code')}</label>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900">
                        {selectedOperation.production_line_lp}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-900 block">{t('imputations.name')}</label>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900">
                        {selectedOperation.activity}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-900 block">{t('imputations.domain')}</label>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900">
                        {selectedOperation.domain?.name || selectedOperation.domain || '-'}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-900 block">{t('imputations.standardTime')}</label>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900">
                        {selectedOperation.temps_standard || '-'}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-900 block">{tCommon('status')}</label>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                          selectedOperation.is_active
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70'
                            : 'bg-red-50 text-red-700 border-red-100 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700/70'
                        }`}>
                          {selectedOperation.is_active ? tCommon('active') : tCommon('inactive')}
                        </span>
                      </div>
                    </div>
                  </div>
                  {selectedOperation.description && (
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-900 block">{tCommon('description')}</label>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900">
                        {selectedOperation.description}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedImputation && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedImputation(null);
              }}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-md w-full border border-slate-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                      <span className="material-symbols-outlined text-red-600 text-[24px]">
                        warning
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900 mb-2">
                        {t('imputations.deleteTitle')}
                      </h3>
                      <p className="text-sm text-slate-600 mb-1">
                        {t('imputations.confirmDeleteWithId', { id: selectedImputation.id })}
                      </p>
                      <div className="text-xs text-slate-500 mt-2 space-y-1">
                        <div>{tCommon('date')}: {selectedImputation.date_operation ? new Date(selectedImputation.date_operation).toLocaleDateString(getDateLocale(i18n.language)) : '-'}</div>
                        <div>{t('imputations.gcrFilter')}: {selectedImputation.gcr || '-'}</div>
                        {selectedImputation.operation && (
                          <div>{t('imputations.operation')}: {selectedImputation.operation.production_line_lp}</div>
                        )}
                      </div>
                      <p className="text-sm text-red-600 font-medium mt-3">
                        {t('imputations.cannotUndo')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setSelectedImputation(null);
                      }}
                      className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteConfirm}
                      disabled={deleting}
                      className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? t('imputations.deleting') : tCommon('delete')}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmployeeImputationsPage;
